import { makeStyles } from '@material-ui/core'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { PageTitle } from '../root/components/common'
import { UserPermissions } from 'web/src/services/permissions'
import { ButtonV2 } from '../root/components/common/button-v2'
import {
    PowerIcon,
    PowerOffIcon,
    RotateCcwIcon,
    WeightIcon
} from 'lucide-react'
import Icon from '@mdi/react'
import { mdiTools } from '@mdi/js'
import {
    confirmLocationMMModeOff,
    confirmLocationMMModeOn,
    confirmLocationPowerOff,
    confirmLocationReplaceBait,
    confirmLocationResetBaitThreshold
} from './api/location-settings-api'
import { enqueueSnackbar } from '../root/actions'
import { LocationConfirmReplaceBaitModal } from './components/location-settings/location-confirm-replace-bait-modal'
import { LocationConfirmThresholdResetModal } from './components/location-settings/location-confirm-threshold-reset-modal'
import { LocationConfirmPowerOffModal } from './components/location-settings/location-confirm-power-off-modal'
import { LocationConfirmMMModeOnModal } from './components/location-settings/location-confirm-mm-mode-on'
import { LocationConfirmMMModeOffModal } from './components/location-settings/location-confirm-mm-mode-off'

const useStyles = makeStyles(theme => ({
    wrapper: {
        maxWidth: 1200
    },
    pageTitle: {
        justifyContent: 'space-between'
    }
}))

function LocationActions(props) {
    const dispatch = useDispatch()
    const classes = useStyles()
    const locationId = props.match.params.locationId

    const theme = useSelector(state => state.main.theme)
    const locale = useSelector(state => state.main.locale)
    const location = useSelector(state => state.locations.location)

    const [
        isConfirmThresholdResetModalOpen,
        setIsConfirmThresholdResetModalOpen
    ] = useState(false)
    const [isConfirmReplaceBaitModalOpen, setIsConfirmReplaceBaitModalOpen] =
        useState(false)
    const [isConfirmPowerOffModalOpen, setIsConfirmPowerOffModalOpen] =
        useState(false)
    const [isConfirmMMModeOnModalOpen, setIsConfirmMMModeOnModalOpen] =
        useState(false)
    const [isConfirmMMModeOffModalOpen, setIsConfirmMMModeOffModalOpen] =
        useState(false)

    const canUpdate = useMemo(() => {
        return UserPermissions.locationV2(
            location?.is_assigned,
            location?.customer_id
        ).update
    }, [location])

    const onConfirmLocationReplaceBait = async () => {
        try {
            const response = await confirmLocationReplaceBait(locationId)
            dispatch(
                enqueueSnackbar({
                    message: `${locale.locations.successWithAmountOfUpdatedDevices} ${response.length}`,
                    options: {
                        variant: 'success'
                    }
                })
            )
        } catch (ex) {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }

    const onConfirmLocationThresholdReset = async () => {
        try {
            const response = await confirmLocationResetBaitThreshold(locationId)
            dispatch(
                enqueueSnackbar({
                    message: `${locale.locations.successWithAmountOfUpdatedDevices} ${response.length}`,
                    options: {
                        variant: 'success'
                    }
                })
            )
        } catch (ex) {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }

    const onConfirmLocationPowerOff = async () => {
        try {
            const response = await confirmLocationPowerOff(locationId)
            dispatch(
                enqueueSnackbar({
                    message: `${locale.locations.successWithAmountOfUpdatedDevices} ${response.length}`,
                    options: {
                        variant: 'success'
                    }
                })
            )
        } catch (ex) {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }

    const onConfirmLocationMMModeOn = async minutes => {
        try {
            const response = await confirmLocationMMModeOn(locationId, minutes)
            dispatch(
                enqueueSnackbar({
                    message: `${locale.locations.successWithAmountOfUpdatedDevices} ${response.length}`,
                    options: {
                        variant: 'success'
                    }
                })
            )
        } catch (ex) {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }

    const onConfirmLocationMMModeOff = async () => {
        try {
            const response = await confirmLocationMMModeOff(locationId)
            dispatch(
                enqueueSnackbar({
                    message: `${locale.locations.successWithAmountOfUpdatedDevices} ${response.length}`,
                    options: {
                        variant: 'success'
                    }
                })
            )
        } catch (ex) {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }

    return (
        <div className={classes.wrapper}>
            <PageTitle
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div>{locale.locations.locationActions}</div>
                        <div
                            style={{
                                color: theme.textPrimaryReverted,
                                fontSize: 14,
                                fontWeight: 600,
                                marginLeft: 10,
                                backgroundColor: `${theme.active}94`,
                                padding: '2px 8px',
                                borderRadius: theme.borderRadius
                            }}>
                            Beta
                        </div>
                    </div>
                }
                className={classes.pageTitle}
            />

            <div style={{ width: '100%', marginTop: 20 }}>
                <ButtonV2
                    onClick={() => setIsConfirmReplaceBaitModalOpen(true)}
                    style={{ width: 600, marginBottom: 20 }}
                    disabled={!canUpdate}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 20
                        }}>
                        <div
                            style={{
                                width: 30,
                                marginRight: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            <WeightIcon size={28} color={theme.primary} />
                        </div>
                        <div>
                            <div style={{ fontSize: 22 }}>
                                {locale.devices.replaceBait}
                            </div>
                            <div>
                                {locale.devices.resetsTheBaitStatusToAvailable}
                            </div>
                        </div>
                    </div>
                </ButtonV2>
                <ButtonV2
                    onClick={() => setIsConfirmThresholdResetModalOpen(true)}
                    style={{ width: 600, marginBottom: 20 }}
                    disabled={!canUpdate}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 20
                        }}>
                        <div
                            style={{
                                width: 30,
                                marginRight: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            <RotateCcwIcon size={28} color={theme.primary} />
                        </div>
                        <div>
                            <div style={{ fontSize: 22 }}>
                                {locale.locations.resetTheActivityThreshold}
                            </div>
                            <div>
                                {
                                    locale.locations
                                        .resetTheActivityThresholdDescr
                                }
                            </div>
                        </div>
                    </div>
                </ButtonV2>
                <ButtonV2
                    onClick={() => setIsConfirmPowerOffModalOpen(true)}
                    style={{ width: 600, marginBottom: 20 }}
                    disabled={!canUpdate}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 20
                        }}>
                        <div
                            style={{
                                width: 30,
                                marginRight: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            <PowerIcon size={28} color={theme.primary} />
                        </div>
                        <div>
                            <div style={{ fontSize: 22 }}>
                                {locale.devices.setOffline}
                            </div>
                            <div>{locale.locations.confirmPowerOffDescr}</div>
                        </div>
                    </div>
                </ButtonV2>
                <ButtonV2
                    onClick={() => setIsConfirmMMModeOnModalOpen(true)}
                    style={{ width: 600, marginBottom: 20 }}
                    disabled={!canUpdate}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 20
                        }}>
                        <div
                            style={{
                                width: 30,
                                marginRight: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            <Icon
                                path={mdiTools}
                                size={'28px'}
                                color={theme.primary}
                            />
                        </div>
                        <div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                <PowerIcon
                                    size={18}
                                    color={theme.primary}
                                    style={{ marginRight: 4 }}
                                />
                                <div style={{ fontSize: 22 }}>
                                    {locale.common.switchOnMMode}
                                </div>
                            </div>
                            <div>{locale.locations.switchMMModeOnDescr}</div>
                        </div>
                    </div>
                </ButtonV2>
                <ButtonV2
                    onClick={() => setIsConfirmMMModeOffModalOpen(true)}
                    style={{ width: 600, marginBottom: 20 }}
                    disabled={!canUpdate}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 20
                        }}>
                        <div
                            style={{
                                width: 30,
                                marginRight: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            <Icon
                                path={mdiTools}
                                size={'28px'}
                                color={theme.primary}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                <PowerOffIcon
                                    size={18}
                                    color={theme.primary}
                                    style={{ marginRight: 4 }}
                                />
                                <div style={{ fontSize: 22 }}>
                                    {locale.common.switchOffMMode}
                                </div>
                            </div>
                            <div>{locale.locations.switchMMModeOffDescr}</div>
                        </div>
                    </div>
                </ButtonV2>
            </div>
            <LocationConfirmReplaceBaitModal
                open={isConfirmReplaceBaitModalOpen}
                onClose={() => setIsConfirmReplaceBaitModalOpen(false)}
                onConfirmClick={onConfirmLocationReplaceBait}
            />
            <LocationConfirmThresholdResetModal
                open={isConfirmThresholdResetModalOpen}
                onClose={() => setIsConfirmThresholdResetModalOpen(false)}
                onConfirmClick={onConfirmLocationThresholdReset}
            />
            <LocationConfirmPowerOffModal
                open={isConfirmPowerOffModalOpen}
                onClose={() => setIsConfirmPowerOffModalOpen(false)}
                onConfirmClick={onConfirmLocationPowerOff}
            />
            <LocationConfirmMMModeOnModal
                open={isConfirmMMModeOnModalOpen}
                onClose={() => setIsConfirmMMModeOnModalOpen(false)}
                onConfirmClick={onConfirmLocationMMModeOn}
            />
            <LocationConfirmMMModeOffModal
                open={isConfirmMMModeOffModalOpen}
                onClose={() => setIsConfirmMMModeOffModalOpen(false)}
                onConfirmClick={onConfirmLocationMMModeOff}
            />
        </div>
    )
}

export default withRouter(LocationActions)
